import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import api from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';
import { toast } from 'sonner';
import {
  Megaphone,
  Play,
  Pause,
  RefreshCw,
  Search,
  Loader2,
  MousePointer,
  DollarSign,
  Eye,
  Copy,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronDown,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Building2,
  Check,
} from 'lucide-react';

const MyCampaigns = () => {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [adAccountFilter, setAdAccountFilter] = useState('all');
  const [controllingCampaign, setControllingCampaign] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Sorting state
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Summary state
  const [summary, setSummary] = useState({
    totalCampaigns: 0,
    activeCampaigns: 0,
    totalImpressions: 0,
    totalClicks: 0,
    totalSpend: 0,
  });

  // Available platforms and ad accounts from campaigns
  const [platforms, setPlatforms] = useState([]);
  const [adAccounts, setAdAccounts] = useState([]);
  
  // Popover states for controlled opening/closing
  const [platformPopoverOpen, setPlatformPopoverOpen] = useState(false);
  const [adAccountPopoverOpen, setAdAccountPopoverOpen] = useState(false);
  const [statusPopoverOpen, setStatusPopoverOpen] = useState(false);

  // Filter ad accounts based on selected platform
  const filteredAdAccounts = platformFilter === 'all' 
    ? adAccounts 
    : adAccounts.filter(acc => acc.platform === platformFilter);

  const advertiserId = user?.advertiser_id;

  // Reset ad account filter when platform changes
  useEffect(() => {
    setAdAccountFilter('all');
  }, [platformFilter]);

  const fetchCampaigns = useCallback(async () => {
    if (!advertiserId) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      
      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (platformFilter && platformFilter !== 'all') {
        params.append('platform', platformFilter);
      }
      if (adAccountFilter && adAccountFilter !== 'all') {
        params.append('ad_account', adAccountFilter);
      }
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      const response = await api.get(`/advertisers/${advertiserId}/campaigns?${params}`);
      const data = response.data;
      
      setCampaigns(data.campaigns || []);
      setTotalCount(data.pagination?.total_count || 0);
      setTotalPages(data.pagination?.total_pages || 0);
      
      // Set ad accounts from API response
      if (data.ad_accounts) {
        setAdAccounts(data.ad_accounts);
      }
      
      // Set platforms from API response (all platforms for this advertiser)
      if (data.platforms && data.platforms.length > 0) {
        setPlatforms(data.platforms);
      } else {
        // Fallback: Extract unique platforms from current page
        const allPlatforms = new Set();
        (data.campaigns || []).forEach(c => {
          (c.platforms || []).forEach(p => allPlatforms.add(p));
        });
        setPlatforms([...allPlatforms]);
      }
      
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [advertiserId, page, pageSize, sortBy, sortOrder, statusFilter, platformFilter, adAccountFilter, searchTerm]);

  const fetchSummary = async () => {
    if (!advertiserId) return;
    
    try {
      const response = await api.get('/dashboard/platform-summary');
      const platformData = response.data || [];
      
      let totalImpressions = 0;
      let totalClicks = 0;
      let totalSpend = 0;
      
      platformData.forEach(p => {
        totalImpressions += p.impressions || 0;
        totalClicks += p.clicks || 0;
        totalSpend += p.spend || 0;
      });
      
      // Get campaign counts
      const summaryResponse = await api.get('/dashboard/summary');
      const summaryData = summaryResponse.data;
      
      setSummary({
        totalCampaigns: summaryData.campaign_counts?.total || 0,
        activeCampaigns: summaryData.campaign_counts?.active || 0,
        totalImpressions,
        totalClicks,
        totalSpend,
      });
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  };

  const handleCampaignControl = async (campaignId, action) => {
    setControllingCampaign(campaignId);
    try {
      const response = await api.post(`/advertisers/${advertiserId}/campaigns/${campaignId}/control?action=${action}`);
      const result = response.data;
      
      if (result.success_count > 0) {
        toast.success(`Campaign ${action === 'start' ? 'started' : 'stopped'} on ${result.success_count} platform(s)`);
        fetchCampaigns();
      } else {
        const errors = result.results?.filter(r => !r.success).map(r => `${r.platform}: ${r.error}`);
        toast.error(`Failed to ${action} campaign: ${errors?.join(', ') || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} campaign`);
    } finally {
      setControllingCampaign(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Campaign ID copied');
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPage(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advertiserId]);

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toLocaleString() || '0';
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const SortableHeader = ({ column, label }) => {
    const isActive = sortBy === column;
    return (
      <th 
        className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase cursor-pointer hover:bg-slate-100 select-none"
        onClick={() => handleSort(column)}
      >
        <div className="flex items-center gap-1">
          {label}
          {isActive ? (
            sortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
          ) : (
            <ArrowUpDown className="w-3 h-3 opacity-30" />
          )}
        </div>
      </th>
    );
  };

  if (!advertiserId) {
    return (
      <Layout>
        <div className="p-6">
          <Card>
            <CardContent className="py-12 text-center">
              <Megaphone className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700">No advertiser account</h3>
              <p className="text-slate-500">Your account is not linked to an advertiser.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">My Campaigns</h1>
            <p className="text-slate-500">Manage and monitor your advertising campaigns</p>
          </div>
          <Button
            variant="outline"
            onClick={() => { fetchCampaigns(); fetchSummary(); }}
            disabled={loading}
            data-testid="refresh-campaigns-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Megaphone className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Total Campaigns</p>
                  <p className="text-xl font-bold text-slate-900">{formatNumber(summary.totalCampaigns)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Play className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Active</p>
                  <p className="text-xl font-bold text-green-600">{formatNumber(summary.activeCampaigns)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Eye className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Impressions</p>
                  <p className="text-xl font-bold text-slate-900">{formatNumber(summary.totalImpressions)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <MousePointer className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Clicks</p>
                  <p className="text-xl font-bold text-slate-900">{formatNumber(summary.totalClicks)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Total Spend</p>
                  <p className="text-xl font-bold text-slate-900">{formatCurrency(summary.totalSpend)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search by name, ID, or ad account..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                      className="pl-9"
                      data-testid="campaign-search-input"
                    />
                  </div>
                  <Button onClick={handleSearch} variant="secondary">
                    Search
                  </Button>
                </div>
              </div>
              
              {/* Platform Filter - First */}
              <Popover open={platformPopoverOpen} onOpenChange={setPlatformPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[180px] justify-between" data-testid="platform-filter">
                    <span className="flex items-center gap-2">
                      <Megaphone className="w-4 h-4 text-slate-500" />
                      {platformFilter === 'all' ? 'All Platforms' : platformFilter}
                    </span>
                    <ChevronDown className="w-4 h-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[200px] p-2" align="start">
                  <Command>
                    <CommandInput placeholder="Search platform..." />
                    <CommandList>
                      <CommandEmpty>No platform found.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem onSelect={() => { setPlatformFilter('all'); setPage(1); setPlatformPopoverOpen(false); }}>
                          <Check className={`mr-2 h-4 w-4 ${platformFilter === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                          All Platforms
                        </CommandItem>
                        {platforms.map(p => (
                          <CommandItem key={p} onSelect={() => { setPlatformFilter(p); setPage(1); setPlatformPopoverOpen(false); }}>
                            <Check className={`mr-2 h-4 w-4 ${platformFilter === p ? 'opacity-100' : 'opacity-0'}`} />
                            {p}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              
              {/* Ad Account Filter - Second (filtered by platform) */}
              {adAccounts.length > 0 && (
                <Popover open={adAccountPopoverOpen} onOpenChange={setAdAccountPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-[200px] justify-between" data-testid="ad-account-filter">
                      <span className="flex items-center gap-2 truncate">
                        <Building2 className="w-4 h-4 text-slate-500 shrink-0" />
                        <span className="truncate">
                          {adAccountFilter === 'all' ? 'All Ad Accounts' : adAccounts.find(a => a.id === adAccountFilter)?.name || 'Select'}
                        </span>
                      </span>
                      <ChevronDown className="w-4 h-4 opacity-50 shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[250px] p-2" align="start">
                    <Command>
                      <CommandInput placeholder="Search ad account..." />
                      <CommandList>
                        <CommandEmpty>No ad account found.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem onSelect={() => { setAdAccountFilter('all'); setPage(1); setAdAccountPopoverOpen(false); }}>
                            <Check className={`mr-2 h-4 w-4 ${adAccountFilter === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                            All Ad Accounts
                          </CommandItem>
                          {filteredAdAccounts.map(acc => (
                            <CommandItem key={acc.id} onSelect={() => { setAdAccountFilter(acc.id); setPage(1); setAdAccountPopoverOpen(false); }}>
                              <Check className={`mr-2 h-4 w-4 ${adAccountFilter === acc.id ? 'opacity-100' : 'opacity-0'}`} />
                              <span className="truncate">{acc.name}</span>
                              {platformFilter === 'all' && acc.platform && (
                                <Badge variant="outline" className="ml-2 text-xs shrink-0">{acc.platform}</Badge>
                              )}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}
              
              {/* Status Filter - Third */}
              <Popover open={statusPopoverOpen} onOpenChange={setStatusPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[150px] justify-between" data-testid="status-filter">
                    <span>{statusFilter === 'all' ? 'All Status' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}</span>
                    <ChevronDown className="w-4 h-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[150px] p-2" align="start">
                  <Command>
                    <CommandList>
                      <CommandGroup>
                        <CommandItem onSelect={() => { setStatusFilter('all'); setPage(1); setStatusPopoverOpen(false); }}>
                          <Check className={`mr-2 h-4 w-4 ${statusFilter === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                          All Status
                        </CommandItem>
                        <CommandItem onSelect={() => { setStatusFilter('active'); setPage(1); setStatusPopoverOpen(false); }}>
                          <Check className={`mr-2 h-4 w-4 ${statusFilter === 'active' ? 'opacity-100' : 'opacity-0'}`} />
                          Active
                        </CommandItem>
                        <CommandItem onSelect={() => { setStatusFilter('paused'); setPage(1); setStatusPopoverOpen(false); }}>
                          <Check className={`mr-2 h-4 w-4 ${statusFilter === 'paused' ? 'opacity-100' : 'opacity-0'}`} />
                          Paused
                        </CommandItem>
                        <CommandItem onSelect={() => { setStatusFilter('completed'); setPage(1); setStatusPopoverOpen(false); }}>
                          <Check className={`mr-2 h-4 w-4 ${statusFilter === 'completed' ? 'opacity-100' : 'opacity-0'}`} />
                          Completed
                        </CommandItem>
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              
              <Select value={pageSize.toString()} onValueChange={(v) => { setPageSize(parseInt(v)); setPage(1); }}>
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Campaigns Table */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold">
                Campaigns ({totalCount.toLocaleString()})
              </CardTitle>
              <div className="text-sm text-slate-500">
                Page {page} of {totalPages}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="py-12 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto" />
                <p className="text-slate-500 mt-2">Loading campaigns...</p>
              </div>
            ) : campaigns.length === 0 ? (
              <div className="py-12 text-center">
                <Megaphone className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-700 mb-2">
                  {totalCount === 0 ? 'No campaigns assigned' : 'No matching campaigns'}
                </h3>
                <p className="text-slate-500">
                  {totalCount === 0 
                    ? 'Contact your administrator to assign campaigns to your account.'
                    : 'Try adjusting your search or filters.'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <SortableHeader column="name" label="Campaign" />
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Campaign ID</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Platforms</th>
                      <SortableHeader column="status" label="Status" />
                      <th 
                        className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase cursor-pointer hover:bg-slate-100"
                        onClick={() => handleSort('impressions')}
                      >
                        <div className="flex items-center justify-end gap-1">
                          Impressions
                          {sortBy === 'impressions' ? (
                            sortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                          ) : (
                            <ArrowUpDown className="w-3 h-3 opacity-30" />
                          )}
                        </div>
                      </th>
                      <th 
                        className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase cursor-pointer hover:bg-slate-100"
                        onClick={() => handleSort('clicks')}
                      >
                        <div className="flex items-center justify-end gap-1">
                          Clicks
                          {sortBy === 'clicks' ? (
                            sortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                          ) : (
                            <ArrowUpDown className="w-3 h-3 opacity-30" />
                          )}
                        </div>
                      </th>
                      <SortableHeader column="total_budget" label="Budget" />
                      <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.map((campaign) => {
                      const hasControlAccess = campaign.platform_budgets?.some(pb => pb.platform_campaign_id);
                      const isActive = campaign.status === 'active';
                      
                      return (
                        <tr 
                          key={campaign.id} 
                          className="border-b border-slate-100 hover:bg-slate-50"
                          data-testid={`campaign-row-${campaign.id}`}
                        >
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium text-slate-900">{campaign.name}</p>
                              {campaign.ad_account_name && (
                                <p className="text-xs text-slate-500 flex items-center gap-1">
                                  <Building2 className="w-3 h-3" />
                                  {campaign.ad_account_name}
                                </p>
                              )}
                              {!campaign.ad_account_name && campaign.client_name && (
                                <p className="text-xs text-slate-500">{campaign.client_name}</p>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-1">
                              <code className="text-xs bg-slate-100 px-2 py-1 rounded font-mono text-slate-600">
                                {campaign.id?.slice(0, 8)}...
                              </code>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0"
                                      onClick={() => copyToClipboard(campaign.id)}
                                    >
                                      <Copy className="w-3 h-3" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Copy full ID</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex flex-wrap gap-1">
                              {campaign.platforms?.map((platform) => (
                                <Badge
                                  key={platform}
                                  variant="secondary"
                                  className="text-xs"
                                >
                                  {platform}
                                </Badge>
                              ))}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <Badge 
                              variant={isActive ? 'default' : 'secondary'}
                              className={isActive ? 'bg-green-100 text-green-700 hover:bg-green-100' : ''}
                            >
                              {isActive && <Play className="w-3 h-3 mr-1" />}
                              {campaign.status === 'paused' && <Pause className="w-3 h-3 mr-1" />}
                              {campaign.status || 'Unknown'}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-right font-mono text-slate-700">
                            {formatNumber(campaign.impressions || 0)}
                          </td>
                          <td className="py-3 px-4 text-right font-mono text-slate-700">
                            {formatNumber(campaign.clicks || 0)}
                          </td>
                          <td className="py-3 px-4 text-right font-mono text-slate-700">
                            {formatCurrency(campaign.total_budget || 0, campaign.advertiser_currency || 'USD')}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-center gap-2">
                              {hasControlAccess ? (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant={isActive ? 'outline' : 'default'}
                                        size="sm"
                                        className={isActive 
                                          ? 'text-amber-600 border-amber-200 hover:bg-amber-50' 
                                          : 'bg-green-600 hover:bg-green-700'}
                                        disabled={controllingCampaign === campaign.id}
                                        onClick={() => handleCampaignControl(campaign.id, isActive ? 'stop' : 'start')}
                                        data-testid={`campaign-control-${campaign.id}`}
                                      >
                                        {controllingCampaign === campaign.id ? (
                                          <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : isActive ? (
                                          <>
                                            <Pause className="w-4 h-4 mr-1" />
                                            Stop
                                          </>
                                        ) : (
                                          <>
                                            <Play className="w-4 h-4 mr-1" />
                                            Start
                                          </>
                                        )}
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>{isActive ? 'Pause this campaign' : 'Start this campaign'}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : (
                                <span className="text-xs text-slate-400 px-2">No control</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                <div className="text-sm text-slate-500">
                  Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalCount)} of {totalCount.toLocaleString()}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(1)}
                    disabled={page === 1}
                  >
                    <ChevronsLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="px-3 py-1 text-sm">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(totalPages)}
                    disabled={page === totalPages}
                  >
                    <ChevronsRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default MyCampaigns;
